/**
 * Centralized file download utility.
 * Replaces duplicated blob-download logic across pages.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Download a file from an authenticated API endpoint.
 * @param {string} path - API path (e.g., "/api/export/all-data")
 * @param {string} filename - Name for the downloaded file
 * @param {object} [options] - Optional fetch options (method, body, headers)
 */
export async function downloadFromApi(path, filename, options = {}) {
  const token = localStorage.getItem("token");
  const url = path.startsWith("http") ? path : `${API_URL}${path}`;

  const response = await fetch(url, {
    method: options.method || "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
    body: options.body,
  });

  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }

  triggerBlobDownload(await response.blob(), filename);
}

/**
 * Trigger a browser download from raw blob data (e.g., from axios responseType: 'blob').
 * @param {Blob} blob
 * @param {string} filename
 */
export function triggerBlobDownload(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, 100);
}

/**
 * Download from a base64-encoded response (e.g., file attachments).
 * @param {string} base64Data - Base64 string of file content
 * @param {string} filename
 * @param {string} contentType - MIME type
 */
export function downloadBase64(base64Data, filename, contentType) {
  const byteChars = atob(base64Data);
  const byteArray = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteArray[i] = byteChars.charCodeAt(i);
  }
  triggerBlobDownload(new Blob([byteArray], { type: contentType }), filename);
}
